// Single source of truth for the services list and each service's detail
// page at /services/<slug>. To add a service, append an object below; the
// list page, the detail page, the sitemap and the prerendered SEO tags all
// pick it up automatically.
export const services = [
  {
    slug: 'web-development',
    title: 'Web Development',
    tagline: 'Fast, modern websites and web apps that convert visitors into customers.',
    icon: 'layout',
    tone: 'blue',
    description:
      'We design and build websites and web applications that load fast, rank well and are easy for your own team to update. From a marketing site to a full web app with logins and dashboards, we handle the design, the build and the deployment.',
    features: [
      { title: 'Custom design', desc: 'A site built around your brand, not a generic template.' },
      { title: 'Responsive by default', desc: 'Looks right on phones, tablets and desktops from day one.' },
      { title: 'SEO foundations', desc: 'Proper titles, meta tags, sitemaps and fast load times built in.' },
      { title: 'Content you can edit', desc: 'Update text, images and pages without calling us for every change.' },
      { title: 'Secure hosting & deployment', desc: 'HTTPS, backups and a deploy process that does not break on update day.' },
      { title: 'Analytics ready', desc: 'Hooked up to the tools you already use to track visitors and conversions.' }
    ],
    process: [
      { title: 'Discover', desc: 'We learn who the site is for and what it needs to do.' },
      { title: 'Design', desc: 'Wireframes and visual design you review before anything is built.' },
      { title: 'Build', desc: 'The site is built page by page, with regular previews.' },
      { title: 'Launch', desc: 'We deploy, test on real devices and hand over access.' }
    ],
    tech: ['React', 'Next.js', 'Node.js', 'Tailwind CSS', 'Vercel'],
    faqs: [
      { q: 'How long does a website take to build?', a: 'A marketing site typically takes two to four weeks from kickoff. A web app with logins and custom features takes longer, depending on scope, which we estimate after a short discovery call.' },
      { q: 'Can I update the content myself after launch?', a: 'Yes. Depending on the site, we either build it on a content management system you can edit directly, or hand over simple instructions for the parts you need to change most often.' },
      { q: 'Do you also handle hosting?', a: 'Yes, we can set up and manage hosting, or deploy to infrastructure you already have. Either way we make sure HTTPS, backups and monitoring are in place before launch.' }
    ],
    relatedProducts: ['learning-web-portal', 'college-erp-core']
  },
  {
    slug: 'saas-products',
    title: 'SaaS Products',
    tagline: 'Cloud software built to go from first customer to thousands, without a rebuild.',
    icon: 'zap',
    tone: 'purple',
    description:
      'We build SaaS products end to end: multi-tenant architecture, subscriptions and billing, and the real-time features modern products need, all built to scale from your first paying customer onward instead of needing a rewrite later.',
    features: [
      { title: 'Multi-tenant architecture', desc: 'One codebase serving every customer, with their data kept properly separate.' },
      { title: 'Subscriptions & billing', desc: 'Plans, trials and payments wired up from the start.' },
      { title: 'Real-time features', desc: 'Live updates, notifications and collaboration where your product needs them.' },
      { title: 'Admin & usage dashboards', desc: 'See who is using what, without digging through a database.' },
      { title: 'Role-based access', desc: 'Owners, team members and guests each see what is relevant to them.' },
      { title: 'API-first design', desc: 'Built so you can add integrations and a public API later without rework.' }
    ],
    process: [
      { title: 'Discover', desc: 'We map out your core workflows and who uses the product.' },
      { title: 'Design', desc: 'Product and UI design focused on the first real workflow end to end.' },
      { title: 'Build', desc: 'An MVP you can put in front of real users quickly, then iterate.' },
      { title: 'Launch & support', desc: 'We stay on to fix issues and add features as you grow.' }
    ],
    tech: ['React', 'Node.js', 'PostgreSQL', 'Realtime Sync', 'Cloud Hosting'],
    faqs: [
      { q: 'Can you build just an MVP first?', a: 'Yes, and we usually recommend it. We help you scope the smallest version that proves your core workflow works, then expand it based on real user feedback rather than guesses.' },
      { q: 'Will the product scale if we grow quickly?', a: 'We build with multi-tenant architecture and a cloud-hosted backend from day one, so growth in users does not require a rebuild, only scaling the infrastructure.' },
      { q: 'Do you handle payments and subscriptions?', a: 'Yes, we integrate the billing and payment flows your product needs, including plans, trials and upgrades.' }
    ],
    relatedProducts: ['learning-app', 'learning-web-portal']
  },
  {
    slug: 'custom-software',
    title: 'Custom Software',
    tagline: 'Enterprise and college ERP systems built around how you already work.',
    icon: 'code',
    tone: 'green',
    description:
      'We build custom software for organizations whose workflows do not fit an off-the-shelf product: college and school ERP systems, internal tools and enterprise applications designed specifically around your process, not a generic template.',
    features: [
      { title: 'Built around your workflow', desc: 'The software adapts to your process instead of the other way around.' },
      { title: 'Automated workflows', desc: 'The manual, repetitive steps your team does today, handled automatically.' },
      { title: 'Legacy data migration', desc: 'Years of existing records moved in cleanly, not re-typed by hand.' },
      { title: 'Admin dashboards', desc: 'A clear view of what is happening across the organization.' },
      { title: 'Role-based access', desc: 'Every user sees exactly what their role needs, nothing more.' },
      { title: 'Ongoing support', desc: 'We stay on after launch to fix issues and add features as needs change.' }
    ],
    process: [
      { title: 'Discover', desc: 'We map your current process and where it breaks down.' },
      { title: 'Design', desc: 'A system designed around your actual workflow, reviewed before we build.' },
      { title: 'Build', desc: 'Iterative development with regular demos so you see progress.' },
      { title: 'Launch & support', desc: 'Data migration, training and support after go-live.' }
    ],
    tech: ['React', 'Node.js', 'PostgreSQL', 'REST APIs', 'Cloud Hosting'],
    faqs: [
      { q: 'What kinds of custom software do you build?', a: 'Most of our custom software work is college and school ERP systems, but we also build internal tools and enterprise applications for other kinds of organizations with workflows that do not fit a generic product.' },
      { q: 'Can you migrate our existing data?', a: 'Yes, this is usually the most important part of the project. We plan data migration as part of the build so your historical records move in cleanly, not as an afterthought.' },
      { q: 'How much does custom software cost?', a: 'It depends mainly on scope: how many user roles, integrations and workflows are involved. Our guide on custom software development cost walks through what actually drives the estimate.' }
    ],
    relatedProducts: ['college-erp-core', 'dcic-platform']
  },
  {
    slug: 'mobile-apps',
    title: 'Mobile Apps',
    tagline: 'Native and cross-platform apps for iOS and Android, built for real daily use.',
    icon: 'smartphone',
    tone: 'blue',
    description:
      'We design and build mobile apps for iOS and Android, from learning and coaching apps to internal tools, focused on the features people actually open every day, not a long list that never gets used.',
    features: [
      { title: 'Cross-platform builds', desc: 'One codebase for iOS and Android where it makes sense.' },
      { title: 'Offline support', desc: 'Content stays usable even without a reliable connection.' },
      { title: 'Push notifications', desc: 'Bring users back for what actually matters to them.' },
      { title: 'In-app payments', desc: 'Subscriptions, batches or one-off purchases, handled in-app.' },
      { title: 'Video & live streaming', desc: 'Live classes, recordings and resource libraries, built in.' },
      { title: 'Play Store & App Store launch', desc: 'We handle listing, submission and release.' }
    ],
    process: [
      { title: 'Discover', desc: 'We identify the handful of features people will actually use daily.' },
      { title: 'Design', desc: 'App screens and flows you review before development starts.' },
      { title: 'Build', desc: 'Iterative builds you can test on a real device throughout.' },
      { title: 'Launch & support', desc: 'Store submission, then fixes and updates after release.' }
    ],
    tech: ['Android', 'React Native', 'Cloud Backend', 'Video Streaming', 'Payment Gateway'],
    faqs: [
      { q: 'Do you build for both iOS and Android?', a: 'Yes. Depending on the project we use native Android development or a cross-platform framework, whichever fits your timeline and feature needs best.' },
      { q: 'Can the app handle live and recorded video?', a: 'Yes, our Learning App is a working example: live classes, automatic recordings and a resource library with offline download, all built in.' },
      { q: 'Do you publish the app for us?', a: 'Yes, we handle Play Store and App Store listing, submission and the review process through to release.' }
    ],
    relatedProducts: ['learning-app']
  },
  {
    slug: 'data-architecture',
    title: 'Data Architecture',
    tagline: 'Database design and backend services built to stay fast as your data grows.',
    icon: 'code',
    tone: 'purple',
    description:
      'We design databases and backend services that stay reliable and fast as your data grows: schema design, API development and query optimisation for products where the data model is the hard part to get right.',
    features: [
      { title: 'Schema design', desc: 'A data model that matches how your product actually uses data.' },
      { title: 'Query optimisation', desc: 'Fast at ten records and fast at ten million.' },
      { title: 'API development', desc: 'Clean REST or GraphQL APIs your frontend and partners can rely on.' },
      { title: 'Data migration', desc: 'Moving data between systems without losing or corrupting it.' },
      { title: 'Backups & recovery', desc: 'A real plan for when something goes wrong, not just hope.' },
      { title: 'Scalable hosting', desc: 'Infrastructure that grows with usage instead of falling over.' }
    ],
    process: [
      { title: 'Audit', desc: 'We review your current data model and where it is straining.' },
      { title: 'Design', desc: 'A schema and API design built for your real query patterns.' },
      { title: 'Build & migrate', desc: 'Implemented and, where needed, existing data moved across safely.' },
      { title: 'Monitor & support', desc: 'We keep an eye on performance after launch, not just at handover.' }
    ],
    tech: ['PostgreSQL', 'MongoDB', 'REST APIs', 'GraphQL', 'Cloud Hosting'],
    faqs: [
      { q: 'Can you fix a slow existing database instead of rebuilding it?', a: 'Often, yes. Many performance problems come down to missing indexes, a few badly-shaped queries or a schema that grew organically. We start with an audit before recommending a rebuild.' },
      { q: 'Do you work with SQL and NoSQL databases?', a: 'Yes, we design with PostgreSQL, MongoDB and similar systems, and pick whichever fits your data and access patterns rather than defaulting to one.' },
      { q: 'Can this be added to an existing product?', a: 'Yes, this service is often brought in alongside an existing team to fix a specific data or API problem, not only as part of building something new.' }
    ],
    relatedProducts: ['college-erp-core', 'devmeter']
  },
  {
    slug: 'tech-consulting',
    title: 'Tech Consulting',
    tagline: 'An outside, technical opinion on your architecture, security and team scaling.',
    icon: 'dashboard',
    tone: 'green',
    description:
      'We provide technical advisory for teams that need an outside, experienced opinion: architecture reviews, security audits and guidance on scaling an engineering team, without committing to a full build.',
    features: [
      { title: 'Architecture review', desc: 'An honest read on what will and will not hold up as you grow.' },
      { title: 'Security audit', desc: 'A practical look for the gaps that actually get exploited.' },
      { title: 'Tech stack guidance', desc: 'Help choosing tools that fit your team, not just what is trending.' },
      { title: 'Code quality review', desc: 'A second pair of experienced eyes on your codebase.' },
      { title: 'Hiring & team scaling advice', desc: 'What to hire for next, and in what order.' },
      { title: 'Written recommendations', desc: 'A clear report you can act on with or without us.' }
    ],
    process: [
      { title: 'Scope', desc: 'We agree exactly what you want reviewed and why.' },
      { title: 'Review', desc: 'We go through your architecture, code or security posture in detail.' },
      { title: 'Report', desc: 'A written report with specific, prioritised recommendations.' },
      { title: 'Walkthrough', desc: 'We go through the findings with your team and answer questions.' }
    ],
    tech: ['Architecture Review', 'Security Audit', 'Performance Review', 'Team Scaling'],
    faqs: [
      { q: 'Do we need to hire Pidesk to build anything?', a: 'No. Consulting is a standalone engagement. Some clients use the findings to guide their own team, others later ask us to build what was recommended, but neither is required.' },
      { q: 'How long does a review take?', a: 'A focused architecture or security review typically takes one to two weeks, depending on the size of the codebase and how much access we are given.' },
      { q: 'What do we get at the end?', a: 'A written report with specific, prioritised findings and recommendations, plus a walkthrough call to go through it with your team.' }
    ],
    relatedProducts: []
  }
];

export const getService = (slug) => services.find((s) => s.slug === slug);
